/**
 * Modal para Aceptación Obligatoria de Políticas y Visión
 * Este componente bloquea el acceso al sistema hasta que el usuario acepte y firme
 */
import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import SignaturePad from './SignaturePad';
import axios from 'axios';
import './PoliticasVisionModal.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:4005' : '');

const PoliticasVisionModal = ({ show, onAccepted, userData }) => {
  const [politica, setPolitica] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Lectura, 2: Firma
  const [firmaData, setFirmaData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      cargarPoliticaActiva();
    }
  }, [show]);

  const cargarPoliticaActiva = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/politicas/version-activa`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        setPolitica(response.data.data);
      } else {
        setError('No hay política activa disponible');
      }
    } catch (err) {
      console.error('Error cargando política:', err);
      setError('Error al cargar el documento de políticas');
    } finally {
      setLoading(false);
    }
  };

  const handleFirmaGuardada = (firmaBase64) => {
    setFirmaData(firmaBase64);
    console.log('✅ Firma capturada');
  };

  const handleFirmaLimpiada = () => {
    setFirmaData(null);
  };

  const handleSubmit = async () => {
    if (!firmaData) {
      alert('Debe firmar el documento para continuar');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      // Obtener IP del cliente (opcional, puedes usar un servicio externo)
      let ipAddress = null;
      try {
        const ipResponse = await axios.get('https://api.ipify.org?format=json');
        ipAddress = ipResponse.data.ip;
      } catch {}

      await axios.post(
        `${API_URL}/api/politicas/firmar`,
        {
          politicaId: politica.id,
          firmaBase64: firmaData,
          ipAddress: ipAddress,
          userAgent: navigator.userAgent
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('✅ Firma registrada exitosamente');
      
      // Notificar que se aceptó correctamente
      if (onAccepted) {
        onAccepted();
      }

    } catch (err) {
      console.error('❌ Error registrando firma:', err);
      setError(err.response?.data?.error || 'Error al registrar la firma');
      setSubmitting(false);
    }
  };

  const getPdfUrl = () => {
    if (!politica) return null;
    const token = localStorage.getItem('token');
    return `${API_URL}/api/politicas/documento/${politica.id}?token=${token}`;
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      size="xl"
      centered
      className="politicas-vision-modal"
    >
      <Modal.Header className="bg-primary text-white">
        <Modal.Title>
          📋 {politica?.titulo || 'Políticas y Visión de la Empresa'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Cargando documento...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {!loading && !error && politica && (
          <>
            <Alert variant="info" className="mb-4">
              <strong>⚠️ Lectura y aceptación obligatoria</strong>
              <p className="mb-0 mt-2">
                Antes de ingresar al sistema, debe leer, aceptar y firmar las políticas
                y visión de la empresa (Versión {politica.version}).
              </p>
            </Alert>

            {step === 1 && (
              <div className="documento-section">
                <h5 className="mb-3">1. Lea el documento completo</h5>
                
                {/* Mostrar texto extraído del PDF */}
                {politica.contenido_texto ? (
                  <div className="text-viewer-container">
                    <div className="text-content">
                      {politica.contenido_texto}
                    </div>
                  </div>
                ) : (
                  <Alert variant="warning">
                    No se pudo extraer el texto del documento. 
                    <a 
                      href={getPdfUrl()} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="alert-link ms-2"
                    >
                      Ver PDF original
                    </a>
                  </Alert>
                )}
                
                <div className="text-center mt-4">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => setStep(2)}
                  >
                    He leído el documento → Continuar
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="firma-section">
                <h5 className="mb-3">2. Firme el documento</h5>
                <p className="text-muted mb-4">
                  Al firmar, usted acepta haber leído y estar de acuerdo con las políticas y visión de la empresa.
                </p>

                <SignaturePad
                  onSave={handleFirmaGuardada}
                  onClear={handleFirmaLimpiada}
                />

                {firmaData && (
                  <Alert variant="success" className="mt-3">
                    ✓ Firma capturada correctamente
                  </Alert>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      setStep(1);
                      setFirmaData(null);
                    }}
                  >
                    ← Volver al documento
                  </Button>
                  
                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={!firmaData || submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Registrando...
                      </>
                    ) : (
                      '✓ Aceptar y Firmar'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <small className="text-muted">
          {userData?.Nombre} - {new Date().toLocaleDateString()}
        </small>
      </Modal.Footer>
    </Modal>
  );
};

export default PoliticasVisionModal;
